/**
 * 블로그 카테고리별 목록 페이지.
 *
 * 영구지침: docs/develop/seo/blog-cms-spec.md §7.1~§7.3, ssr-spec.md §15.2
 */
import { Link, useParams } from 'react-router-dom'
import { BookOpen, Calendar, User } from 'lucide-react'
import SubpageHero from '@/components/common/SubpageHero'
import PageContainer from '@/components/layout/PageContainer'
import Spinner from '@/components/ui/Spinner'
import SEO from '@/components/common/SEO'
import { BreadcrumbJsonLd } from '@/components/common/JsonLd'
import { useBlogPosts } from '@/features/blog/api'
import { BLOG_CATEGORIES, BLOG_CATEGORY_LABELS, type BlogCategory } from '@/lib/blog/types'
import NotFoundPage from '@/pages/NotFoundPage'

export default function BlogCategoryPage() {
  const { category } = useParams<{ category: string }>()

  // 무효 카테고리는 404 (soft-404 방지). <Navigate> 는 SSR(StaticRouter)에서 아무것도 하지 않아
  // 홈 기본 title 의 빈 페이지가 200 으로 나갔다.
  if (!category || !BLOG_CATEGORIES.includes(category as BlogCategory)) {
    return (
      <NotFoundPage
        title="블로그 카테고리를 찾을 수 없습니다"
        description="주소가 잘못되었거나 더 이상 운영하지 않는 카테고리입니다."
      />
    )
  }

  return <BlogCategoryList category={category as BlogCategory} />
}

/**
 * 유효한 카테고리의 글 목록.
 *
 * <p>카테고리 검사(조기 return)와 데이터 훅을 한 컴포넌트에 두면, 같은 화면에서 카테고리가
 * 유효↔무효로 바뀔 때 렌더마다 훅 개수가 달라져 React 가 화면을 중단한다(#300/#310).
 * 검사는 바깥 컴포넌트가, 훅은 이 컴포넌트가 맡아 훅 호출이 항상 무조건 실행되게 한다.
 * 무효 카테고리에서 불필요한 목록 API 호출도 생기지 않는다.</p>
 */
function BlogCategoryList({ category: cat }: { category: BlogCategory }) {
  const label = BLOG_CATEGORY_LABELS[cat]
  // 쿼리 키는 SSR 프리페치(entry-server.tsx)의 blogPostsQueryKey({ category, size: 100 }) 와 동일해야 한다
  const { data: page, isLoading } = useBlogPosts({ category: cat, size: 100 })
  const posts = page?.content ?? []

  return (
    <>
      <SEO
        title={`${label} - 요양 정보 블로그`}
        description={`${label} 관련 모든 글을 한 곳에서 확인하세요.`}
        url={`/blog/category/${cat}`}
      />
      <BreadcrumbJsonLd
        items={[
          { name: '홈', url: '/' },
          { name: '블로그', url: '/blog' },
          { name: label, url: `/blog/category/${cat}` },
        ]}
      />
      <SubpageHero
        title={label}
        description={`${label} 카테고리의 모든 글`}
        icon={<BookOpen size={20} className="text-white" />}
      />
      <PageContainer noMaxWidth>
        <div className="max-w-6xl mx-auto">
        <div className="my-6">
          <Link to="/blog" className="text-sm text-cb-primary hover:underline">
            ← 블로그 목록으로
          </Link>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            이 카테고리에 아직 발행된 글이 없습니다
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
            {posts.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group rounded-2xl border border-gray-200 bg-white overflow-hidden hover:shadow-lg transition-shadow"
              >
                {post.thumbnailUrl ? (
                  <div className="aspect-[16/9] overflow-hidden bg-gray-100">
                    <img
                      src={post.thumbnailUrl}
                      alt={post.title}
                      loading="lazy"
                      className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-cb-primary-light to-white flex items-center justify-center">
                    <BookOpen size={48} className="text-cb-primary/30" />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="text-base font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-cb-primary transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {post.summary}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <User size={12} />
                      {post.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {post.publishedAt?.slice(0, 10) ?? ''}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
        </div>
      </PageContainer>
    </>
  )
}
